import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import api, { axiosInstance } from '@api'
import type {
  AcceptInvitation,
  JWT,
  Login,
  Register,
  TokenRefresh,
  UserDetails,
  MicrosoftLogin,
  MicrosoftTenantResponse } from '@apiTypes'
import {
  WorkspacePreferencesAuthProviderEnum,
} from '@apiTypes'
import router from '@/router'
import { publicPages } from '@/router/public-pages'
import { useAuthStore } from '@store/auth'
import storage from '@/utils/storage'
import * as Sentry from '@sentry/vue'
import { clearPusher, getActivePusherSocketId } from '@/composables/push/pusher'
import { useSocialStore } from '@store/social'
import { useAIStore } from '@store/ai'
import { routeRedirectLocation } from '@/utils/router.utils'

/**
 * Return if user is logged in
 * This is completely up to you and how you want to store the token in your frontend application
 * e.g. If you are using cookies to store the application please update this function
 */
const accessTokenKey = 'accessToken'
const refreshTokenKey = 'refreshToken'
const accessToken = () => storage.getItem(accessTokenKey)
const refreshToken = () => storage.getItem(refreshTokenKey)
export const useAccessToken = useStorage<string>(accessTokenKey, null, storage)
useAccessToken.value = accessToken()
export const useRefreshToken = useStorage<string>(refreshTokenKey, null, storage)
useRefreshToken.value = refreshToken()
export const isUserLoggedIn = () => !!useAccessToken.value
export const hasRefreshToken = () => !!useRefreshToken.value

export function loginHelper(data: Login): Promise<UserDetails> {
  return new Promise((resolve, reject) => {
    api.auth.authLoginCreate(data, { skipAuthToken: true })
      .then(r => {
        handleJWTResponse(r.data)
        resolve(r.data.user)
      })
      .catch(err => {
        reject(err)
      })
  })
}

export function refreshTokenHelper(): Promise<TokenRefresh> {
  const data: TokenRefresh = {
    refresh: useRefreshToken.value || '',
    access: useAccessToken.value || '',
  }

  console.log('[AXIOS] Response Interceptors - Refreshing token')

  return new Promise((resolve, reject) => {
    api.auth.authTokenRefreshCreate(data, { skipAuthRefresh: true })
      .then(r => {
        handleTokens(r.data)
        resolve(r.data)
      })
      .catch(err => {
        reject(err)
      })
  })
}

export function registerHelper(data: Register): Promise<UserDetails> {
  return new Promise((resolve, reject) => {
    api.auth.authRegisterCreate(data, { skipAuthToken: true })
      .then(r => {
        handleJWTResponse(r.data)
        resolve(r.data.user)
      })
      .catch(err => {
        reject(err)
      })
  })
}

export function invitationHelper(data: AcceptInvitation): Promise<UserDetails> {
  return new Promise((resolve, reject) => {
    api.invitations.invitationsAcceptCreate(data, { skipAuthToken: true })
      .then(r => {
        handleJWTResponse(r.data)
        resolve(r.data.user)
      })
      .catch(err => {
        reject(err)
      })
  })
}

export function microsoftLoginHelper(data: MicrosoftLogin): Promise<UserDetails> {
  return new Promise((resolve, reject) => {
    api.auth.authSocialMicrosoftCreate(data, { skipAuthToken: true })
      .then(r => {
        handleJWTResponse(r.data)
        resolve(r.data.user)
      })
      .catch(err => {
        reject(err)
      })
  })
}

export async function microsoftTenantInfoHelper(data: MicrosoftLogin): Promise<MicrosoftTenantResponse> {
  const socialStore = useSocialStore()

  const maxRetries = 30
  const retryInterval = 1000 // ms

  let currentRetries = 0

  const response_token = await api.auth.authSocialMicrosoftAccessTokenCreate(data)

  while (currentRetries < maxRetries) {
    try {
      const response = await api.auth.authSocialMicrosoftInfoCreate({ accessToken: response_token.data.accessToken })

      socialStore.clearTenantInfo()

      return response.data
    }
    catch {
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, retryInterval))
    }

    currentRetries++
  }

  socialStore.clearTenantInfo()
  throw new Error('Timed out trying to fetch Microsoft tenant info')
}

function handleJWTResponse(data: JWT) {
  const { access, refresh, user } = data

  const authStore = useAuthStore()

  authStore.setCurrentUser(user)
  handleTokens({ access, refresh })
  Sentry.setUser({ id: user.id, email: user.email })
}

function handleTokens(data: { refresh?: string; access?: string }) {
  if (data.access)
    useAccessToken.value = data.access

  if (data.refresh)
    useRefreshToken.value = data.refresh
}

export async function logoutHelper() {
  const refresh = refreshToken()

  // Clear tokens before redirect; keep session so the navbar doesn't flash.
  clearAuthTokens()

  await router.push({ name: 'login' })

  clearAuthSession()

  if (!refresh)
    return

  try {
    await axiosInstance.post(
      '/api/v1/auth/logout/',
      { refresh },
      { skipAuthToken: true, skipLogout: true },
    )
  }
  catch (error) {
    console.warn('[AUTH] Backend logout failed', error)
  }
}

export function switchWorkspaceHelper(id: number): Promise<AxiosResponse<UserDetails>> {
  return new Promise((resolve, reject) => {
    api.auth.authUserPartialUpdate({ workspaceId: id })
      .then(r => {
        resolve(r)
      })
      .catch(err => {
        reject(err)
      })
  })
}

export function reloadPageAfterWorkspaceChange(route: RouteLocationNormalizedLoaded) {
  location.assign(routeRedirectLocation(route).path)
}

export function clearAuthDataAndRedirect() {
  clearAuthData()
  router.push({ name: 'login' })
}

export function clearAuthTokens() {
  useAccessToken.value = null
  useRefreshToken.value = null
}

export function clearAuthSession() {
  useAuthStore().clear()

  // Workspace-scoped and feature-gating, so it must not outlive the session: logging in as someone
  // else in the same tab is an in-SPA router.push, not a reload.
  useAIStore().resetRateLimit()
  clearPusher()
  Sentry.setUser(null)
}

export function clearAuthData() {
  clearAuthTokens()
  clearAuthSession()
}

export function shouldLogoutOn401(): boolean {
  const routeName = router.currentRoute.value?.name
  if (!routeName)
    return true

  const isPublicRoute = publicPages.some(route => RegExp(route).test(String(routeName)))

  return !isPublicRoute
}

export function setHeaders(config: AxiosRequestConfig) {
  const authStore = useAuthStore()

  config.headers = config.headers || {}
  config.headers['Accept-Language'] = authStore.getCurrentLanguage
  config.headers['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone

  const socketId = getActivePusherSocketId()
  if (socketId) {
    config.headers['X-Pusher-Socket-Id'] = socketId
  }

  const token = useAccessToken.value

  if (!token)
    return // Exit early if there's no token

  // Type assertion to indicate that headers are guaranteed to be defined
  const headers = config.headers

  if (!config.skipAuthToken) {
    headers.Authorization = `Bearer ${token}`
  }
}

export function getSupportedLoginProviders(): string[] {
  return (import.meta.env.VITE_SUPPORTED_LOGIN_PROVIDERS || '').split(',')
}

export function isProviderEnabled(provider: WorkspacePreferencesAuthProviderEnum): boolean {
  return getSupportedLoginProviders().includes(provider.toLowerCase())
}

export function isOnlyMicrosoftLoginEnabled(): boolean {
  return isProviderEnabled(WorkspacePreferencesAuthProviderEnum.MICROSOFT)
    && !isProviderEnabled(WorkspacePreferencesAuthProviderEnum.PASSWORD)
}

export function isOnlyPasswordLoginEnabled(): boolean {
  return isProviderEnabled(WorkspacePreferencesAuthProviderEnum.PASSWORD)
    && !isProviderEnabled(WorkspacePreferencesAuthProviderEnum.MICROSOFT)
}
